import type { ToolConfigPreferences } from '@timelyapp/integration-cloud-api-spec';

// Define our own SettingsRangeEnums to include naming_strategy values
type SettingsRangeEnums = 'default' | 'high' | 'medium' | 'low' | 'off' | 'title' | 'tool' | 'hide';

type SettingProperty = keyof Pick<
  ToolConfigPreferences,
  'continuity' | 'noise_cancellation' | 'naming_strategy'
>;

// For all settings
type SettingsRangeKey = Exclude<SettingsRangeEnums, 'default'>;

/**
 * Type for a setting option
 */
export type SettingOption = {
  value: number | string;
  label: string;
  sidenote: string;
  [key: string]: any; // Allow for additional properties
};

/**
 * Type for a setting's metadata
 */
export type SettingMetadata = {
  title: string;
  description: string;
  defaultValue: string;
};

/**
 * Type for a normalized setting result
 */
export type SettingResult = {
  // Metadata about the setting
  metadata: SettingMetadata;
  // All available options for this setting
  options: Array<SettingOption & { key: string }>;
  // The current value (numeric)
  value: number | string;
  // The display label
  label: string;
  // The sidenote (e.g. "5min")
  sidenote: string;
  // The key (e.g. "high", "medium", "low", "off", "custom", "default")
  key: string;
};

/**
 * Single source of truth for settings properties.
 *
 * This structure contains all information about each setting property:
 * - The numeric value for each range setting
 * - The display label for each range setting
 * - The sidenote for each range setting
 * - Metadata about the setting
 */
// Define a more flexible type for the settings definitions
type SettingsDefinition = {
  metadata: SettingMetadata;
  ranges: Record<string, SettingOption>;
};

const settingsDefinitions: Record<SettingProperty, SettingsDefinition> = {
  continuity: {
    metadata: {
      title: 'Continuity',
      description: 'Memories continue over brief gaps in tracked activities.',
      defaultValue: 'low', // Default from API schema is 2, which corresponds to 'low'
    },
    ranges: {
      high: { value: 16, label: 'Extended', sidenote: '16min' },
      medium: { value: 8, label: 'Regular', sidenote: '8min' },
      low: { value: 2, label: 'Short', sidenote: '2min' },
      off: { value: 0, label: 'Off', sidenote: 'No merge' },
    },
  },
  noise_cancellation: {
    metadata: {
      title: 'Noise cancellation',
      description: 'Reduces distraction in your timeline by hiding shorter Memories.',
      defaultValue: 'low', // Default from API schema is 2, which corresponds to 'low'
    },
    ranges: {
      high: { value: 6, label: 'High', sidenote: '6min' },
      medium: { value: 4, label: 'Medium', sidenote: '4min' },
      low: { value: 2, label: 'Low', sidenote: '2min' },
      off: { value: 0, label: 'Off', sidenote: 'All details' },
    },
  },
  naming_strategy: {
    metadata: {
      title: 'Title by',
      description: 'Personalize the titles for Memories in your Timeline.',
      defaultValue: 'title',
    },
    ranges: {
      title: { value: 1, label: 'File or page name', sidenote: 'File or page name' },
      tool: { value: 2, label: 'App or website', sidenote: 'App or website name' },
    },
  },
};

/**
 * Core function to get all information about a setting.
 *
 * This function normalizes any input value (range setting, number, or default)
 * and returns a complete SettingResult with all information about the setting.
 *
 * @param property The setting property
 * @param value The value to resolve (can be a range setting, number, or undefined)
 * @param memoryFilterValue The value from the global memory filter (used as the actual default)
 * @param defaultValue The default value to use if value is 'default' and memoryFilterValue is not provided
 * @returns A complete SettingResult with all information about the setting
 */
export function getSetting(
  property: SettingProperty,
  value?: SettingsRangeEnums | number,
  memoryFilterValue?: number | string,
  defaultValue?: SettingsRangeEnums | number
): SettingResult {
  const settingDef = settingsDefinitions[property];
  const allOptions = Object.entries(settingDef.ranges).map(([key, option]) => ({
    ...option,
    key,
  }));

  // Get the default option from the memory filter value if available
  const getDefaultOption = () => {
    // If we have a memory filter value, use that as the default
    if (memoryFilterValue !== undefined) {
      // Find the option that matches the memory filter value
      const option = findOptionByValue(memoryFilterValue);
      if (option) {
        return option;
      }

      // If no exact match, create a custom option
      return createCustomOption(memoryFilterValue);
    }

    // Otherwise use the setting definition's default
    const defaultKey = settingDef.metadata.defaultValue;
    return {
      ...settingDef.ranges[defaultKey],
      key: defaultKey,
    };
  };

  // Find an option by its value (numeric or string)
  const findOptionByValue = (val: number | string) => {
    if (typeof val === 'number') {
      return allOptions.find(option => option.value === val);
    } else if (typeof val === 'string') {
      return allOptions.find(option => option.key === val);
    }
    return undefined;
  };

  // Find the closest option to a numeric value
  const findClosestOption = (val: number) => {
    let closestOption = allOptions[0];

    if (typeof closestOption.value !== 'number') {
      return closestOption;
    }

    let closestDiff = Math.abs(closestOption.value - val);

    for (const option of allOptions) {
      if (typeof option.value !== 'number') continue;
      const diff = Math.abs(option.value - val);
      if (diff < closestDiff) {
        closestDiff = diff;
        closestOption = option;
      }
    }

    return closestOption;
  };

  // Create a custom option for a value
  const createCustomOption = (val: number | string): SettingOption => {
    if (typeof val === 'number') {
      const closestOption = findClosestOption(val);
      return {
        value: val,
        label: `Custom (${val})`,
        sidenote: `${val}min`,
        key: 'custom',
      };
    } else {
      // For string values
      return {
        value: 0, // Default numeric value
        label: `Custom (${val})`,
        sidenote: val,
        key: val,
      };
    }
  };

  // Resolve the default value
  const resolveDefaultValue = (): SettingOption => {
    // If we have a memory filter value, use that but mark it as 'default'
    if (memoryFilterValue !== undefined) {
      // Find the option that matches the memory filter value
      const matchingOption = findOptionByValue(memoryFilterValue);
      if (matchingOption) {
        // Create a special 'default' option that shows what the default actually is
        return {
          value: matchingOption.value,
          label: 'Default',
          sidenote: `${matchingOption.label}`,
          key: 'default',
        };
      } else {
        // If no exact match, create a custom default option
        return {
          value: memoryFilterValue,
          label: 'Default',
          sidenote: `Custom (${memoryFilterValue})`,
          key: 'default',
        };
      }
    }

    // If no memory filter value but defaultValue is provided
    if (defaultValue !== undefined) {
      if (typeof defaultValue === 'string' && defaultValue !== 'default') {
        const option = settingDef.ranges[defaultValue as SettingsRangeKey];
        if (option) {
          return {
            ...option,
            key: defaultValue,
            sidenote: `${option.sidenote} (${option.label})`,
          };
        }
      }

      if (typeof defaultValue === 'number') {
        const option = findOptionByValue(defaultValue);
        if (option) {
          return {
            ...option,
            sidenote: `${option.sidenote} (${option.label})`,
          };
        }
        return createCustomOption(defaultValue);
      }
    }

    // If no memory filter value and no defaultValue, use the setting's default
    return getDefaultOption();
  };

  // Resolve the current option based on the input value
  let currentOption: SettingOption;

  // Case 1: No value provided, use default
  if (value === undefined) {
    currentOption = getDefaultOption();
  }
  // Case 2: Value is 'default', use the resolved default value
  else if (value === 'default') {
    currentOption = resolveDefaultValue();
  }
  // Case 3: Value is a range setting
  else if (typeof value === 'string') {
    const option = settingDef.ranges[value as SettingsRangeKey];
    if (option) {
      currentOption = { ...option, key: value };
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`getSetting: "${property}":"${value}" not found in settingsDefinitions`);
      }
      currentOption = getDefaultOption();
    }
  }
  // Case 4: Value is a number
  else if (typeof value === 'number') {
    const option = findOptionByValue(value);
    if (option) {
      currentOption = option;
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.warn(`getSetting: "${property}":"${value}" not found in settingsDefinitions`);
      }
      currentOption = createCustomOption(value);
    }
  }
  // Case 5: Unexpected value type, use default
  else {
    currentOption = getDefaultOption();
  }

  // Create the options array
  let options = [...allOptions];

  // Add a 'default' option if memoryFilterValue is provided
  if (memoryFilterValue !== undefined) {
    const matchingOption = findOptionByValue(memoryFilterValue);
    if (matchingOption) {
      // Add the default option at the beginning of the array
      options = [
        {
          value: matchingOption.value,
          label: 'Default',
          sidenote: `${matchingOption.label}`,
          key: 'default',
        },
        ...options,
      ];
    }
  }

  // Return the complete setting result
  return {
    metadata: settingDef.metadata,
    options,
    value: currentOption.value,
    label: currentOption.label,
    sidenote: currentOption.sidenote,
    key: currentOption.key,
  };
}

/**
 * Converts ambigious properties with relative values to a number.
 *
 * Used to convert some ToolConfigPreferences properties to numbers
 * the Memory parser system can understand.
 * @deprecated
 */
export const mapSettingsRangeToNumber = (
  property: SettingProperty,
  rangeSetting: SettingsRangeEnums,
  defaultValue: number
): number => {
  if (rangeSetting === 'default') return defaultValue;

  const result = getSetting(property, rangeSetting, undefined, defaultValue);
  return result.value as number;
};

/**
 * Converts a number back to the corresponding range setting.
 *
 * Used to convert numeric values back to ToolConfigPreferences properties.
 * @deprecated
 */
export const mapNumberToRangeSetting = (
  property: SettingProperty,
  value: number,
  defaultValue: SettingsRangeEnums
): SettingsRangeEnums => {
  const result = getSetting(property, value, undefined, defaultValue);
  return result.key === 'custom' ? defaultValue : (result.key as SettingsRangeEnums);
};

/**
 * Converts a number to a human-readable label for display.
 *
 * This function first converts the number to a range setting,
 * then maps that setting to a display label.
 * @deprecated
 */
export const mapNumberToSettingsLabel = (
  property: SettingProperty,
  value: number,
  defaultValue: SettingsRangeEnums
): string => {
  const result = getSetting(property, value, undefined, defaultValue);
  return result.label;
};

/**
 * Converts a range setting directly to a human-readable label for display.
 *
 * This allows components to get display labels without needing to write
 * conditional logic for each property and range value.
 * @deprecated
 */
export const mapRangeSettingToLabel = (
  property: SettingProperty,
  rangeSetting: SettingsRangeEnums
): string => {
  const result = getSetting(property, rangeSetting);
  return result.label;
};
